import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlineGalleryHighlightEditor from "@/components/admin/Content/WebPages/InlineEditors/InlineGalleryHighlightEditor";
import { useHomePageHighlights } from "@/lib/api/hooks/gallery";
import { GalleryHighlight } from "@/lib/api/types";

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
};

const ImageHighlight = ({ data }: { data: WebsitePageSection }) => {
    // Fetch live gallery highlights
    const { data: highlightsData, isLoading } = useHomePageHighlights();
    const galleryHighlights = highlightsData?.data || [];

    const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png";

    return (
        <EditableSection
            sectionTag={data.sectionTag}
            contentType={data.contentType}
            data={data}
            customEditor={InlineGalleryHighlightEditor}
        >
            <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="flex flex-col gap-2">
                    <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">
                        Image Highlights ({galleryHighlights.length}/4)
                    </Paragraph>
                    <div className="flex items-center gap-6">
                        <Heading level={4}>{data.sectionTitle ?? "Image Highlights"}</Heading>
                    </div>
                    <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Featured image highlights for the home page")} />
                </div>

                {/* Gallery Preview */}
                {isLoading ? (
                    <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#681628]"></div>
                        <p className="text-sm text-gray-500 mt-2">Loading images...</p>
                    </div>
                ) : galleryHighlights.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {galleryHighlights.map((gallery: GalleryHighlight, index: number) => (
                            <div key={gallery.galleryId} className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded w-fit">
                                    Image {index + 1}
                                </span>
                                <div className="relative w-full h-24 bg-gray-200 rounded overflow-hidden">
                                    <img
                                        src={gallery.photoUrl || defaultImage}
                                        alt={gallery.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            if (e.currentTarget.src !== defaultImage) {
                                                e.currentTarget.src = defaultImage;
                                            }
                                        }}
                                    />
                                </div>
                                <h5 className="font-semibold text-xs text-gray-900 truncate">{gallery.title}</h5>
                                {(gallery.datePublished || gallery.publishedByName) && (
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                        {gallery.datePublished && (
                                            <p className="text-xs text-gray-500">
                                                <span className="font-medium">Published:</span> {formatPublishedDate(gallery.datePublished)}
                                            </p>
                                        )}
                                        {gallery.publishedByName && (
                                            <p className="text-xs text-gray-500">
                                                <span className="font-medium">By:</span> {gallery.publishedByName}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-6 text-center bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500 text-sm">No images selected. Click to select up to 4 galleries.</p>
                    </div>
                )}
            </div>
        </EditableSection>
    )
}

export default ImageHighlight
